<template>
  <!-- Partitions: the workspaces under this client, who is in them, and how many the plan allows (US 9365) -->
  <div class="us-page">
    <section class="home-hero us-hero">
      <div class="home-hero__main">
        <div class="home-hero__eyebrow">{{ $t('maintenance_overview.eyebrow', { name: getClient.name }) }}</div>
        <h1 class="home-hero__title" qid="partition-page-title">{{ $t('maintenance.partitions.title') }}</h1>
        <p class="home-hero__summary">{{ summaryText }}</p>

        <div class="home-search us-search">
          <font-awesome-icon icon="search" class="home-search__icon"/>
          <input ref="search"
                 v-model="keyword"
                 class="home-search__input"
                 :placeholder="$t('maintenance.partitions.search_placeholder')"
                 :aria-label="$t('maintenance.partitions.search_placeholder')"
                 autocomplete="off"
                 qid="partitions-search-input"
                 @input="debounceInput"
          />
          <span class="home-kbd">/</span>
        </div>

        <div class="home-hero__chips">
          <button v-if="isGranted(permissions.PARTITION_CREATE)"
                  type="button"
                  class="home-chip home-chip--light"
                  qid="partition-create-button"
                  @click="$refs.form.showCreatePartitionModal()"
          >
            <font-awesome-icon icon="plus"/>{{ $t('administration.partitions.create_button') }}
          </button>
          <GuideButton v-if="getGuide('partitions_list')" :guide="getGuide('partitions_list')"></GuideButton>
        </div>
      </div>

      <div v-if="limit" class="home-week us-health" qid="partitions-plan">
        <div class="home-week__top">
          <svg class="home-ring"
               width="96"
               height="96"
               viewBox="0 0 96 96"
               aria-hidden="true"
          >
            <circle class="home-ring__track"
                    cx="48"
                    cy="48"
                    r="42"
            />
            <circle v-if="allCount"
                    class="home-ring__value"
                    :class="{ 'is-full': allCount >= limit }"
                    cx="48"
                    cy="48"
                    r="42"
                    :stroke-dasharray="ringDash(allCount, limit, 42)"
            />
          </svg>
          <b class="home-week__count us-health__count">{{ allCount }}/{{ limit }}</b>
          <div>
            <div class="home-week__title">{{ $t('partitions_page.plan.title') }}</div>
            <div class="home-week__text">
              {{ allCount >= limit ? $t('partitions_page.plan.full') : $tc('partitions_page.plan.left', limit - allCount, { count: limit - allCount }) }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="v2-card us-card">
      <div class="us-table-wrap">
        <table class="us-table pt-table">
          <thead>
            <tr>
              <th v-for="column in visibleColumns"
                  :key="column.key"
                  :class="{ 'is-sortable': column.sort }"
                  :aria-sort="column.sort && selectedSort === column.key ? (selectedDirection === 'asc' ? 'ascending' : 'descending') : null"
                  :qid="'sort-p-' + column.key"
                  @click="column.sort && selectSort(column.key)"
              >
                {{ $t(column.label) }}
                <font-awesome-icon v-if="column.sort && selectedSort === column.key"
                                   :icon="selectedDirection === 'asc' ? 'caret-up' : 'caret-down'"
                                   class="ml-1"
                />
              </th>
              <th class="us-table__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="partition in tableItems"
                :key="partition.id"
                class="pt-row"
                qid="partitions-row"
                @click="openMembers(partition, $event)"
            >
              <td>
                <div class="us-who">
                  <WorkspaceAvatar :workspace="partition" :size="34" :partition="true"/>
                  <span class="pt-name">
                    <router-link :to="membersRoute(partition)" class="us-who__name" qid="partitions-name">{{ partition.name }}</router-link>
                    <span v-if="partition.reference_number" class="pt-sub">{{ $t('partitions_page.reference', { ref: partition.reference_number }) }}</span>
                  </span>
                </div>
              </td>
              <td class="us-muted" qid="partitions-cvr">{{ partition.cvr || '–' }}</td>
              <td qid="partitions-country">
                <span v-if="partition.country || partition.city" class="pt-place">
                  <img v-if="flagOf(partition)"
                       :src="flagOf(partition)"
                       height="12"
                       alt=""
                  >
                  <span>
                    {{ [partition.city, partition.country].filter(Boolean).join(', ') }}
                  </span>
                </span>
                <span v-else class="us-muted">–</span>
              </td>
              <td>
                <span class="pt-members" :title="(partition.members || []).map(m => m.name).join(', ')">
                  <span class="pt-stack">
                    <span v-for="member in (partition.members || []).slice(0, 4)"
                          :key="member.id"
                          class="us-avatar pt-stack__avatar"
                          :style="{ backgroundImage: 'url(' + avatarUrl(member) + ')' }"
                    ></span>
                  </span>
                  <span class="us-muted">{{ $tc('partitions_page.members_count', partition.members_count || 0, { count: partition.members_count || 0 }) }}</span>
                </span>
              </td>
              <td v-if="getClient.projectModule" qid="partitions-projects">
                <span v-if="partition.open_projects" class="pt-projects">
                  {{ $tc('partitions_page.open_projects', partition.open_projects, { count: partition.open_projects }) }}
                  <span v-if="partition.overdue_projects" class="v2-tag v2-tag--red">{{ $tc('partitions_page.overdue', partition.overdue_projects, { count: partition.overdue_projects }) }}</span>
                </span>
                <span v-else class="us-muted">–</span>
              </td>
              <td qid="partitions-last-activity">
                <div v-if="partition.last_activity"
                     class="us-last"
                     v-b-tooltip.hover
                     :title="formatDateTime(partition.last_activity.at)"
                >
                  <span class="us-last__date">{{ partition.last_activity.at | formatRelativeDate }}</span>
                  <span v-if="partition.last_activity.by" class="us-last__ago">{{ $t('partitions_page.activity_by', { name: partition.last_activity.by }) }}</span>
                </div>
                <span v-else class="us-muted">{{ $t('partitions_page.no_activity') }}</span>
              </td>
              <td class="us-table__actions">
                <router-link :to="membersRoute(partition)"
                             class="us-icon-btn"
                             v-b-tooltip.hover
                             :title="$t('partitions_page.actions.members')"
                             :aria-label="$t('partitions_page.actions.members')"
                             qid="partitions-members-button"
                >
                  <font-awesome-icon icon="users"/>
                </router-link>
                <button v-if="isGranted(permissions.PARTITION_EDIT)"
                        type="button"
                        class="us-icon-btn"
                        v-b-tooltip.hover
                        :title="$t('users_page.actions.edit')"
                        :aria-label="$t('users_page.actions.edit')"
                        qid="partitions-edit-button"
                        @click="$refs.form.showEditPartitionModal(partition)"
                >
                  <font-awesome-icon icon="pen"/>
                </button>
                <b-dropdown v-if="isGranted(permissions.PARTITION_DELETE)"
                            right
                            no-caret
                            variant="link"
                            toggle-class="us-icon-btn"
                            qid="partitions-more-button"
                >
                  <template #button-content>
                    <font-awesome-icon icon="ellipsis-h"/>
                    <span class="sr-only">{{ $t('users_page.actions.more') }}</span>
                  </template>
                  <b-dropdown-item variant="danger" qid="partitions-delete-button" @click="showConfirmDeleteModal(partition)">
                    <font-awesome-icon icon="trash-alt" class="mr-2"/>{{ $t('partitions_page.actions.delete') }}
                  </b-dropdown-item>
                </b-dropdown>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length" qid="no-results-row">
              <td :colspan="visibleColumns.length + 1" class="us-empty">
                <template v-if="keyword">{{ $t('system.no_results') }}</template>
                <div v-else class="pt-empty">
                  <span class="pt-empty__icon"><font-awesome-icon icon="layer-group"/></span>
                  <b>{{ $t('partitions_page.empty.title') }}</b>
                  <span>{{ $t('partitions_page.empty.text', { client: getClient.name }) }}</span>
                  <b-button v-if="isGranted(permissions.PARTITION_CREATE)"
                            variant="success"
                            @click="$refs.form.showCreatePartitionModal()"
                  >
                    <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('administration.partitions.create_button') }}
                  </b-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalRows" class="us-foot">
        <span class="us-foot__count">{{ $t('users_page.pagination', { from: rangeFrom, to: rangeTo, total: totalRows }) }}</span>
        <multiselect v-model="perPage"
                     class="us-filter us-filter--small"
                     :options="perPageOptions"
                     track-by="value"
                     label="value"
                     :allow-empty="false"
                     :placeholder="selectedPerPage"
                     :select-label="$t('system.dropdown_select_label')"
                     :selected-label="$t('system.dropdown_selected_label')"
                     :deselect-label="$t('system.dropdown_deselect_label')"
                     qid="partitions-per-page"
                     @select="perPageChanged"
        ></multiselect>
        <span class="us-grow"></span>
        <b-pagination v-model="currentPage"
                      class="us-pagination"
                      :total-rows="totalRows"
                      :per-page="Number(selectedPerPage)"
                      limit="5"
                      @change="loadContent"
        ></b-pagination>
      </div>
    </div>

    <PartitionFormModal ref="form" buttonType="none" @saved="reload"></PartitionFormModal>

    <!-- Delete: the whole partition goes, so the name has to be typed -->
    <b-modal ref="delete-partition" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="delete-partition-title">{{ $t('administration.partitions.delete.title') }}</h5>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.partitions.delete.cancel')"
                  @click="hideConfirmDeleteModal"
          >
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-form">
        <div class="us-remove">
          <WorkspaceAvatar :workspace="itemToDelete" :size="40" :partition="true"/>
          <div>
            <b>{{ itemToDelete.name }}</b>
            <div class="us-muted">{{ $tc('partitions_page.members_count', itemToDelete.members_count || 0, { count: itemToDelete.members_count || 0 }) }}</div>
          </div>
        </div>
        <div class="us-note pt-note--danger">
          <font-awesome-icon icon="exclamation-triangle"/>
          <span>{{ $t('partitions_page.delete.text') }}</span>
        </div>
        <b-form-group class="mb-0">
          <label class="us-field-label" for="delete-partition-name">{{ $t('partitions_page.delete.type_name', { name: itemToDelete.name }) }}</label>
          <b-form-input id="delete-partition-name"
                        v-model="deleteConfirmation"
                        autocomplete="off"
                        qid="delete-partition-confirm-input"
          ></b-form-input>
        </b-form-group>
        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="delete-partition-cancel-button" @click="hideConfirmDeleteModal">
            {{ $t('administration.partitions.delete.cancel') }}
          </b-button>
          <b-button variant="danger"
                    :disabled="deleteConfirmation.trim() !== (itemToDelete.name || '').trim() || buttonDisabled"
                    qid="delete-partition-submit-button"
                    @click="confirmDeletePartition(itemToDelete)"
          >
            {{ $t('partitions_page.actions.delete') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import {userAvatarUrl} from '@/util/userAvatar.js';
  import GuideButton from '@/components/guide/GuideButton.vue';
  import WorkspaceAvatar from '@/components/navigation/WorkspaceAvatar.vue';
  import PartitionFormModal from '@/components/partition/PartitionFormModal.vue';
  import {userMeParams, linkParents} from '@/util/userClients.js';

  export default {
    name: 'Partitions',
    components: {
      GuideButton,
      WorkspaceAvatar,
      PartitionFormModal
    },
    data: function() {
      return {
        tableItems: [],
        columns: [
          { key: 'name', label: 'administration.partitions.create.name_label', sort: true },
          { key: 'cvr', label: 'administration.partitions.create.cvr_label' },
          { key: 'country', label: 'partitions_page.location' },
          { key: 'members', label: 'partitions_page.members' },
          { key: 'projects', label: 'partitions_page.projects', module: 'projectModule' },
          { key: 'last_activity', label: 'partitions_page.last_activity' }
        ],
        keyword: '',
        selectedSort: 'name',
        selectedDirection: 'asc',
        countries: [],
        limit: 0,
        allCount: 0,

        perPageOptions: [
          { value: '10' },
          { value: '20' },
          { value: '50' },
          { value: '100' }
        ],
        perPage: null,
        selectedPerPage: '10',
        currentPage: 1,
        totalRows: 0,

        itemToDelete: {},
        deleteConfirmation: '',
        loading: true,
        buttonDisabled: false,
        myGuides: []
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      getStorePerPage() {
        return this.$store.state.perPage;
      },
      summaryText() {
        let text = this.$tc('partitions_page.summary', this.allCount, { count: this.allCount, client: this.getClient.name });

        if (this.limit && this.allCount < this.limit) {
          text += ' ' + this.$tc('partitions_page.plan.left_sentence', this.limit - this.allCount, { count: this.limit - this.allCount });
        }
        return text;
      },
      // The projects column only where the Projects module is on
      visibleColumns() {
        return this.columns.filter(column => !column.module || this.getClient[column.module]);
      },
      rangeFrom() {
        return (this.currentPage - 1) * Number(this.selectedPerPage) + 1;
      },
      rangeTo() {
        return Math.min(this.rangeFrom + Number(this.selectedPerPage) - 1, this.totalRows);
      }
    },
    watch: {
      getClient: function () {
        this.reload();
      }
    },
    methods: {
      debounceInput: _.debounce(function() {
        this.loadContent(1);
      }, 400),
      reload() {
        this.loadContent(this.currentPage);
      },
      avatarUrl(user) {
        return userAvatarUrl(user);
      },
      formatDateTime(date) {
        return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
      },
      membersRoute(partition) {
        return { name: 'maintenance.partitionMembers', params: { slug: partition.slug } };
      },
      // The whole row opens the partition, except clicks on its buttons and links
      openMembers(partition, event) {
        if (!event.target.closest('a, button, .dropdown')) {
          this.$router.push(this.membersRoute(partition));
        }
      },
      flagOf(partition) {
        let country = partition.country && _.find(this.countries, { name: partition.country });
        return country ? country.flag : null;
      },
      ringDash(done, total, radius) {
        let circumference = 2 * Math.PI * radius;
        return circumference * Math.min(done / total, 1) + ' ' + circumference;
      },
      getGuide(code) {
        return _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Maintenance' && object.enabled;
        });
      },
      loadGuides(code) {
        let self = this;
        piincHttp.get('guides', { params: { per_page: 1, language: 'en', type: 'Maintenance', scope: code, client: this.getClient.slug } }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0]);
          }
        }, function() {});
      },
      loadCountries() {
        let self = this;
        piincHttp.get('countries-with-flag').then(function(response) {
          self.countries = response.data;
        }, function() {});
      },
      loadContent(page) {
        let self = this;
        this.currentPage = page || 1;

        piincHttp.get('partitions', { params: {
          page: this.currentPage,
          per_page: this.selectedPerPage,
          sort: this.selectedSort || undefined,
          order: this.selectedDirection || undefined,
          keyword: this.keyword || undefined,
          with_overview: 1,
          client: this.getClient.slug
        }}).then(function(response) {
          self.tableItems = response.data.items;
          self.totalRows = response.data.total_count;
          self.limit = response.data.limit || 0;
          self.allCount = response.data.all_count || 0;
          self.loading = false;
        }, function() {});
      },
      selectSort(sort) {
        if (sort === this.selectedSort) {
          this.selectedDirection = this.selectedDirection === 'desc' ? 'asc' : 'desc';
        } else {
          this.selectedDirection = sort === 'name' ? 'asc' : 'desc';
          this.selectedSort = sort;
        }
        this.loadContent(1);
      },
      perPageChanged(perPage) {
        this.selectedPerPage = perPage.value;
        this.$store.commit('storePerPage', this.selectedPerPage);
        this.loadContent(1);
      },
      showConfirmDeleteModal(partition) {
        this.itemToDelete = partition;
        this.deleteConfirmation = '';
        this.$refs['delete-partition'].show();
      },
      hideConfirmDeleteModal() {
        this.$refs['delete-partition'].hide();
      },
      confirmDeletePartition(partition) {
        let self = this;
        this.buttonDisabled = true;

        piincHttp.delete('partitions/' + partition.slug).then(function() {
          // The workspace switcher lists partitions, so refresh the user's workspaces
          piincHttp.get('users/me', { params: userMeParams() }).then(function(response) {
            let user = response.data.user;
            user.clients = linkParents(response.data.clients);
            self.$store.commit('storeUser', user);
          }, function() {});
          self.buttonDisabled = false;
          self.hideConfirmDeleteModal();
          self.reload();
          self.$toastr('success', self.$t('administration.partitions.delete.success'));
        }, function() {
          self.buttonDisabled = false;
        });
      },
      // "/" jumps to the search, unless the user is typing somewhere
      focusSearch(event) {
        let tag = event.target && event.target.tagName;

        if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !(event.target && event.target.isContentEditable)) {
          event.preventDefault();
          this.$refs.search.focus();
        }
      }
    },
    created() {
      if (this.getStorePerPage) {
        this.selectedPerPage = this.getStorePerPage;
      }
      this.loadCountries();
      this.loadContent();
      this.loadGuides('partitions_list');
    },
    mounted() {
      window.addEventListener('keydown', this.focusSearch);
    },
    beforeDestroy() {
      window.removeEventListener('keydown', this.focusSearch);
    }
  }
</script>
